import React, { useRef, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  Dimensions,
  Animated,
  Easing,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme';
import { SettingsContent } from './SettingsContent';
import { ContactsList, Contact as ContactsListContact } from './ContactsList';
import { useLocale } from '../contexts/LocaleContext';
import { useAppStore } from '../store/appStore';
import { PANE_ANIMATION_DURATION } from '../constants/paneLayout';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useProfileCompletion } from '../hooks/useProfileCompletion';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobileBrowser = isWeb && screenWidth < 768;

type LeftPaneContent = 'settings' | 'contacts' | 'custom';

interface Contact {
  id: string;
  name: string;
  channel: 'whatsapp' | 'facebook-messenger' | 'x-twitter' | 'phone' | 'telegram' | 'email';
  phoneNumber?: string;
  email?: string;
  profilePictureUrl?: string;
  isOnline?: boolean;
  isBusiness?: boolean;
  metadata?: any;
}

interface LeftPaneProps {
  isVisible?: boolean;
  onClose?: () => void;
  content?: LeftPaneContent;
  customContent?: React.ReactNode;
  onSettingsItemPress?: (itemId: string) => void;
  activeSettingsItemId?: string | null;
  /** Vertical offset (px) from the top of the viewport, on web only — defaults to 180 to
   * sit below the app's fixed header. Screens that hide the header entirely (e.g. the flow
   * editor, which needs the full viewport height for its canvas) pass 0. */
  topOffset?: number;
  /** content="contacts" passthrough — mirrors RightPane's own contacts props
   * exactly (see its own doc comment on why this pane hosts contacts at all:
   * putting the list on the leading-edge pane, rather than RightPane's
   * trailing edge, is what makes it show on the left in English and the
   * right in Arabic for free, via this component's existing RTL mirroring
   * below, with no extra per-language logic needed). */
  onContactPress?: (contact: Contact) => void;
  contactsDataListId?: string;
  contactsRefreshTrigger?: number;
  /** Id of the contact currently open elsewhere — shows a visual "active"
   * mark on that row (see ContactsList's own activeContactId doc comment). */
  activeContactId?: string;
}

export const LeftPane: React.FC<LeftPaneProps> = ({
  isVisible = true,
  onClose,
  content = 'settings',
  customContent,
  onSettingsItemPress,
  activeSettingsItemId,
  topOffset = 180,
  onContactPress,
  contactsDataListId,
  contactsRefreshTrigger,
  activeContactId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { isRTL } = useLocale();
  const { leftPaneCollapsed, leftPaneWidth, setLeftPaneCollapsed } = useAppStore();
  const { phoneAccount } = useProfileCompletion();
  const hasPhoneService = phoneAccount?.status === 'active';

  // Selection mode for bulk contact actions — same pattern as RightPane's
  // own contacts view (purely a UI concern local to this pane's content).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  useEffect(() => {
    if (!isVisible || content !== 'contacts') {
      exitSelectionMode();
    }
  }, [isVisible, content]);

  const handleToggleSelect = (contact: ContactsListContact) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(contact.id)) next.delete(contact.id);
      else next.add(contact.id);
      return next;
    });
  };

  const handleSelectAllFiltered = (contacts: ContactsListContact[]) => {
    setSelectedIds(new Set(contacts.map(c => c.id)));
  };

  const widthAnim = useRef(new Animated.Value(leftPaneCollapsed ? 0 : leftPaneWidth)).current;

  useEffect(() => {
    Animated.timing(widthAnim, {
      toValue: leftPaneCollapsed ? 0 : leftPaneWidth,
      duration: PANE_ANIMATION_DURATION,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start();
  }, [leftPaneCollapsed, leftPaneWidth, widthAnim]);

  // Continuous subtle pulse on the grabber bar to draw the eye to it —
  // same opacity-loop pattern as PulsingAccentBar.tsx elsewhere in the app.
  const grabberPulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(grabberPulse, { toValue: 0.5, duration: 900, useNativeDriver: true }),
        Animated.timing(grabberPulse, { toValue: 1, duration: 900, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [grabberPulse]);

  if (!isVisible || (Platform.OS === 'web' && isMobileBrowser)) {
    return null;
  }

  const renderContent = () => {
    if (content === 'custom') {
      return customContent || null;
    }
    if (content === 'contacts') {
      return (
        <ContactsList
          showHeader={true}
          showSearch={true}
          showFilters={true}
          refreshTrigger={contactsRefreshTrigger}
          onContactPress={onContactPress}
          hasPhoneService={hasPhoneService}
          activeContactId={activeContactId}
          compact
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onSelectAllFiltered={handleSelectAllFiltered}
          onEnterSelectionMode={() => setSelectionMode(true)}
          onExitSelectionMode={exitSelectionMode}
        />
      );
    }
    return (
      <SettingsContent
        variant="pane"
        showHeader={true}
        onClose={onClose}
        onItemPress={onSettingsItemPress}
        activeItemId={activeSettingsItemId}
      />
    );
  };

  // In RTL: pane anchors to right edge, border is on left side
  // In LTR: pane anchors to left edge, border is on right side
  const rtlStyle = isWeb
    ? {
        [isRTL ? 'right' : 'left']: 0,
        borderRightWidth: isRTL ? 0 : 1,
        borderLeftWidth: isRTL ? 1 : 0,
        borderRightColor: colors.border,
        borderLeftColor: colors.border,
      }
    : {};

  // Starts below the fixed web header (topOffset — the header's real
  // measured height, passed down from ScreenLayout) and spans to the
  // bottom of the viewport, so pane content is never hidden behind the header.
  const offsetStyle = isWeb
    ? { top: topOffset, bottom: 0 }
    : {};

  // Full-height grabber bar on the pane's inner edge (facing main content)
  // — the right edge in LTR, left edge in RTL — outside the animated-width
  // container so it stays clickable as the pane collapses to 0. Spans the
  // whole pane height (not a small floating circle) so it reads as a
  // deliberate panel-edge control, matching VS Code/Slack-style sidebars.
  const grabberSide = isRTL ? 'left' : 'right';
  const chevronIcon = leftPaneCollapsed
    ? (isRTL ? 'chevron-back' : 'chevron-forward')
    : (isRTL ? 'chevron-forward' : 'chevron-back');

  return (
    <Animated.View style={[styles.wrapper, rtlStyle, offsetStyle, { width: widthAnim }]} pointerEvents="box-none">
      <View style={styles.container}>
        {renderContent()}
      </View>
      {isWeb && !isMobileBrowser && (
        <TouchableOpacity
          style={[styles.grabberHit, { [grabberSide]: -7 } as any]}
          onPress={() => setLeftPaneCollapsed(!leftPaneCollapsed)}
          activeOpacity={0.85}
        >
          <Animated.View style={[styles.grabberIconWrap, { opacity: grabberPulse }]}>
            <Ionicons name={chevronIcon as any} size={12} color={colors.text.onPrimary as string} />
          </Animated.View>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    // height: '100%' is native-only — on web the wrapper is positioned via
    // top/bottom (offsetStyle, computed from topOffset), and combining that
    // with height:100% made the box taller than the viewport, pushing
    // content below the visible area. top+bottom alone fully determines box height.
    ...(!isWeb && { height: '100%' }),
    ...(isWeb && {
      position: 'fixed' as any,
      zIndex: 90,
      overflow: 'visible' as any,
    }),
  },
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  grabberHit: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 14,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 91,
    ...(isWeb && { cursor: 'pointer' as any }),
  },
  grabberIconWrap: {
    width: 20,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
